/** Owns the existing realtime socket, authentication readiness, queue and reconnect lifecycle. */
import { websocket, ConnectionMessageType, SystemMessageType, isSensitiveMessage } from '@playcastdotio/Messaging';
import type { WebsocketMessage } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { getStateStore } from '../../api/stores';
import { machineIdState } from '../../api/stores/shared';
import * as Sentry from '@sentry/react';
import { attachSentryTraceToMessage } from '../../api/modules/realtime/tracing';
import { clearPendingHostAuth } from '../../api/modules/realtime/pendingHostAuth';
import { serviceContainer } from '../ServiceContainer';
import { dispatchRealtimeMessage } from './RealtimeMessageDispatcher';
import { restorePeerData } from './RealtimePeerSession';
import { RealtimeReconnectPolicy } from './RealtimeReconnectPolicy';
import { RealtimeHeartbeatController } from './RealtimeHeartbeatController';

const HEARTBEAT_INTERVAL = 10000; // 10 seconds between pings
const HEARTBEAT_RESPONSE_DEADLINE_MS = 25000;
const RECONNECT_BASE_DELAY = 3000; // initial delay before reconnecting
const RECONNECT_MAX_DELAY = 60000; // cap at 1 minute between retries
const AUTH_DEADLINE_MS = 15000; // 15 seconds to complete the auth handshake

/** Guest mode context for existing Sentry diagnostics. */
const getGuestModeContext = () => {
  const state = getStateStore('ipc');
  return {
    guest_mode_enabled: state.hostMatchmakingState.currentState.guestAccount,
    edition: state.hostState.currentState.edition,
    ad_mode_enabled: state.adModeState.currentState.enabled,
    game_launched: state.playjectorHostModeState.currentState.gameLaunched,
    user_logged_in: state.playjectorHostModeState.currentState.userLoggedIn,
    steam_launched: state.playjectorHostModeState.currentState.steamLaunched,
    host_status: state.hostState.currentState.status,
    matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
  };
};

let wsConnection: WebSocket | null = null;
let isReady = false;
let messageQueue: WebsocketMessage[] = [];
let reconnectTimeout: NodeJS.Timeout | null = null;
let authDeadlineTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
/** Delay last passed to setTimeout for the armed reconnect (for floor upgrades). */
let scheduledReconnectDelayMs = 0;
/** Date.now() when the current reconnect timer was armed. */
let reconnectScheduledAtMs = 0;
const reconnectPolicy = new RealtimeReconnectPolicy(RECONNECT_BASE_DELAY, RECONNECT_MAX_DELAY);

const connectionListeners: Array<() => void> = [];
const disconnectionListeners = new Set<() => void>();
const hostAuthSuccessListeners: Array<() => void> = [];
const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const armReconnectTimeout = (delayMs: number) => {
  clearReconnectTimeout();
  scheduledReconnectDelayMs = delayMs;
  reconnectScheduledAtMs = Date.now();
  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null;
    scheduledReconnectDelayMs = 0;
    // A synchronous WebSocket constructor failure must be able to schedule the
    // next attempt from connect()'s catch path.
    isReconnecting = false;
    try {
      console.log('[Realtime] Attempting to reconnect now');
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Realtime] Reconnection attempt failed:', error);
      Sentry.captureException(error);
      handleReconnect();
    }
  }, delayMs);
};

const startAuthDeadline = () => {
  clearAuthDeadline();
  authDeadlineTimeout = setTimeout(() => {
    console.error(`[Realtime] Auth handshake did not complete within ${AUTH_DEADLINE_MS}ms — reconnecting`);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: 'Auth handshake timed out',
      level: 'error',
      data: { deadlineMs: AUTH_DEADLINE_MS, ...getGuestModeContext() },
    });
    Sentry.captureMessage('[Realtime] Auth handshake timed out', { level: 'warning' });
    // Drop pending AUTH context so an in-flight AUTH_CHALLENGE signer cannot
    // call disconnect()/handleReconnect() and cancel this reconnect.
    clearPendingHostAuth();
    handleReconnect();
  }, AUTH_DEADLINE_MS);
};

const clearAuthDeadline = () => {
  if (authDeadlineTimeout) {
    clearTimeout(authDeadlineTimeout);
    authDeadlineTimeout = null;
  }
};

/**
 * Restart the auth handshake deadline while one is already active (e.g. when
 * AUTH_CHALLENGE arrives and AIK signing may need several more seconds).
 * No-op if auth is not in progress.
 */
export const bumpAuthDeadline = (): void => {
  if (!authDeadlineTimeout) return;
  startAuthDeadline();
};

const heartbeat = new RealtimeHeartbeatController(HEARTBEAT_INTERVAL, HEARTBEAT_RESPONSE_DEADLINE_MS, {
  isOpen: () => wsConnection?.readyState === WebSocket.OPEN,
  sendPing: () => {
    const connection = wsConnection;
    if (!connection || connection.readyState !== WebSocket.OPEN) throw new Error('Socket unavailable');
    const message = websocket.generators.system(SystemMessageType.SYS_PING, {});
    attachSentryTraceToMessage(message);
    connection.send(JSON.stringify(message));
  },
}, failure => {
  console.error(failure.reason === 'response_timeout'
    ? `[Realtime] Heartbeat response did not arrive within ${HEARTBEAT_RESPONSE_DEADLINE_MS}ms — reconnecting`
    : '[Realtime] Failed to send heartbeat:', failure.reason === 'send_failed' ? failure.error : '');
  Sentry.captureMessage(`[Realtime] Heartbeat ${failure.reason}`, { level: 'warning', extra: getGuestModeContext() });
  // handleReconnect closes the socket; do not call disconnect() first — that
  // would clear an already-armed reconnect timer and can strand retries.
  handleReconnect();
});

/**
 * Close the socket and clear auth/queue state without touching reconnect
 * scheduling. Used by handleReconnect so disconnect() is not required (and
 * cannot cancel an armed reconnect timer).
 */
const closeSocketOnly = () => {
  heartbeat.stop();
  clearAuthDeadline();
  setIsReady(false);
  messageQueue = [];

  const conn = wsConnection;
  wsConnection = null;
  if (!conn) return;

  // Drop handlers before close so a late event cannot touch the replacement socket.
  conn.onopen = null;
  conn.onmessage = null;
  conn.onerror = null;
  conn.onclose = null;
  try {
    conn.close();
  } catch (error) {
    console.error('[Realtime] Error while closing connection:', error);
  }
};

export type HandleReconnectOptions = {
  /** Floor the scheduled delay (e.g. soft-fatal attestation auth still retries, but slower). */
  minDelayMs?: number;
};

export const handleReconnect = (options?: HandleReconnectOptions) => {
  const minDelayMs = options?.minDelayMs ?? 0;

  if (isReconnecting) {
    if (minDelayMs <= 0) {
      console.log('[Realtime] Reconnection already in progress');
      return;
    }
    const elapsed = Date.now() - reconnectScheduledAtMs;
    const remaining = Math.max(0, scheduledReconnectDelayMs - elapsed);
    if (minDelayMs <= remaining) {
      console.log('[Realtime] Reconnection already in progress with sufficient delay');
      return;
    }
    console.log(`[Realtime] Raising reconnect delay floor from remaining ${remaining}ms to ${minDelayMs}ms`);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: 'Raising reconnect delay floor',
      level: 'info',
      data: { remainingMs: remaining, minDelayMs, ...getGuestModeContext() },
    });
    armReconnectTimeout(minDelayMs);
    return;
  }

  const decision = reconnectPolicy.next();
  const delayMs = Math.max(decision.delayMs, minDelayMs);
  isReconnecting = true;
  if (decision.firstAtMaximumDelay) {
    Sentry.captureMessage('[Realtime] Reconnect delay capped; continuing retries', {
      level: 'warning',
      tags: { reconnect_delay_capped: 'true' },
      extra: { attempts: decision.attempt, ...getGuestModeContext() },
    });
  }

  console.log(`[Realtime] Initiating reconnection attempt ${decision.attempt} in ${delayMs}ms`);
  Sentry.addBreadcrumb({
    category: 'websocket',
    message: 'Initiating websocket reconnection',
    level: 'info',
    data: {
      attempt: decision.attempt,
      delay: delayMs,
      minDelayMs: options?.minDelayMs,
      ...getGuestModeContext(),
    },
  });

  // Close socket without going through disconnect() — disconnect clears the
  // reconnect timer and would race with soft-fatal / deadline callers.
  closeSocketOnly();
  armReconnectTimeout(delayMs);
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Realtime] Connection already exists');
    return;
  }

  restorePeerData();

  try {
    wsConnection = new WebSocket(constants.REALTIME_API_WEBSOCKET_URL);

    wsConnection.onopen = () => {
      // Always wait for the next tick to ensure the connection is fully established
      setTimeout(() => {
        if (wsConnection?.readyState === WebSocket.OPEN) {
          console.log('[Realtime] Connected, sending init message');
          Sentry.addBreadcrumb({
            category: 'websocket',
            message: 'Realtime websocket connected',
            level: 'info',
            data: {
              url: constants.REALTIME_API_WEBSOCKET_URL,
              version: constants.VERSION,
              ...getGuestModeContext(),
            },
          });
          const msg = websocket.generators.connection(ConnectionMessageType.CONN_INIT, {
            version: constants.VERSION,
          });
          attachSentryTraceToMessage(msg);
          wsConnection?.send(JSON.stringify(msg));
          startAuthDeadline();
        } else {
          console.warn('[Realtime] Connection not ready after open event');
          Sentry.addBreadcrumb({
            category: 'websocket',
            message: 'Websocket not ready after open event',
            level: 'warning',
            data: getGuestModeContext(),
          });
          handleReconnect();
        }
      }, 0);
    };

    wsConnection.onmessage = (event) => {
      const validation = websocket.validateWebsocketMessage(event.data);
      if (!validation.success) {
        console.error('[Realtime] Invalid message:', isSensitiveMessage(event.data) ? '[private message]' : validation.error?.message ?? 'unknown');
        return;
      }

      const message = validation.data;
      if (!message) {
        console.error('[Realtime] No message data');
        return;
      }

      if (message.type === SystemMessageType.SYS_PONG) {
        heartbeat.recordPong();
        return;
      }

      dispatchRealtimeMessage(message, wsConnection, handleReconnect);
    };

    wsConnection.onclose = () => {
      console.log('[Realtime] WebSocket closed');
      Sentry.addBreadcrumb({
        category: 'websocket',
        message: 'Realtime websocket closed',
        level: 'warning',
        data: {
          willReconnect: !isReconnecting,
          ...getGuestModeContext(),
        },
      });
      setIsReady(false);
      heartbeat.stop();

      serviceContainer.analyticsManager.sendNetworkDisconnectedEvent(machineIdState.currentState);

      // Only attempt reconnect if we weren't already trying to disconnect
      if (!isReconnecting) {
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Realtime] WebSocket error:', error);
      Sentry.addBreadcrumb({
        category: 'websocket',
        message: 'Realtime websocket error',
        level: 'error',
        data: {
          error: error?.toString() || 'Unknown error',
          ...getGuestModeContext(),
        },
      });
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Realtime] Failed to connect:', error);
    handleReconnect();
  }
};

const processQueue = () => {
  while (messageQueue.length > 0 && isReady && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        attachSentryTraceToMessage(message);
        wsConnection.send(JSON.stringify(message));
      } catch (error) {
        console.error('[Realtime] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

export const disconnect = () => {
  // Keep isReconnecting in sync with the timer: clearing the timer without
  // resetting the flag strands future handleReconnect() calls.
  clearReconnectTimeout();
  isReconnecting = false;
  scheduledReconnectDelayMs = 0;
  closeSocketOnly();
};

export const setIsReady = (ready: boolean) => {
  const wasReady = isReady;
  isReady = ready;
  if (!ready) heartbeat.stop();
  if (wasReady && !ready) {
    for (const listener of disconnectionListeners) {
      try { listener(); } catch { console.error('[Realtime] Disconnection listener failed'); }
    }
  }
  if (isReady) {
    heartbeat.start();
    const isReconnect = reconnectPolicy.attemptCount > 0;
    reconnectPolicy.reset();
    clearAuthDeadline();
    processQueue();

    serviceContainer.analyticsManager.sendNetworkConnectedEvent(isReconnect);

    // Notify connection listeners (for coordinator architecture)
    connectionListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('[Realtime] Connection listener error:', error);
      }
    });
  }
};

export const onConnected = (listener: () => void): (() => void) => {
  connectionListeners.push(listener);

  return () => {
    const index = connectionListeners.indexOf(listener);
    if (index > -1) {
      connectionListeners.splice(index, 1);
    }
  };
};

export const onHostAuthSuccess = (listener: () => void): (() => void) => {
  hostAuthSuccessListeners.push(listener);
  return () => {
    const index = hostAuthSuccessListeners.indexOf(listener);
    if (index > -1) {
      hostAuthSuccessListeners.splice(index, 1);
    }
  };
};

export const notifyHostAuthSuccess = (): void => {
  hostAuthSuccessListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('[Realtime] Host auth success listener error:', error);
    }
  });
};

export const sendMessage = (message: WebsocketMessage) => {
  if (isSensitiveMessage(message)) { trySendMessage(message); return; }
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isReady) {
    console.warn('[Realtime] Connection not ready, queueing message');
    messageQueue.push(message);
    return;
  }

  // Automatically attach Sentry trace context to ALL outgoing messages
  // This enables distributed tracing across the full connection lifecycle
  attachSentryTraceToMessage(message);

  try {
    wsConnection.send(JSON.stringify(message));
  } catch (error) {
    console.error('[Realtime] Failed to send message:', error);
    messageQueue.push(message);
  }
};
/** Authenticated immediate send: neither queues nor replays after reconnect. */
export function trySendMessage(message: WebsocketMessage): boolean {
  if (!isConnectionReady()) return false;
  try {
    if (!isSensitiveMessage(message)) attachSentryTraceToMessage(message);
    wsConnection!.send(JSON.stringify(message));
    return true;
  } catch { disconnect(); handleReconnect(); return false; }
}
export function isConnectionReady(): boolean {
  return isReady && wsConnection?.readyState === WebSocket.OPEN;
}
export function onDisconnected(listener: () => void): () => void {
  disconnectionListeners.add(listener);
  return () => { disconnectionListeners.delete(listener); };
}
export { requestPeerId, storePeerData, clearPeerData, sendHostMetricBatchWebrtcStats } from './RealtimePeerSession';
export type { WebrtcQualityStatsMetric } from './RealtimePeerSession';
